/**
 * useScrollCompletion Hook
 * Tracks scroll-based completion for non-interactive content blocks
 * Requirements: 2.1, 2.2, 2.3
 *
 * This hook:
 * - Uses IntersectionObserver to detect when block bottom passes viewport
 * - Fires completion callback once per block (idempotent)
 * - Handles cleanup on unmount
 */

import { useEffect, useRef, RefObject } from 'react';
import { isVisibleIntersectionUncovered } from '../intersectionVisibility';
import { measureStickyTopBandHeight } from '../narrationScroll';

/**
 * NA-363: a block may mark a descendant with this attribute to move its
 * scroll-completion boundary. The block then completes when the ANCHOR's
 * bottom edge enters the viewport instead of the block's own — e.g. the
 * Lesson Complete block completes as soon as its Continue button is fully
 * visible, not when the padding below the button has also scrolled in.
 */
export const SCROLL_COMPLETION_ANCHOR_ATTRIBUTE = 'data-scroll-completion-anchor';

/**
 * Hook to track scroll-based completion for content blocks
 *
 * Uses IntersectionObserver to detect when a block's bottom edge scrolls
 * above the viewport bottom, indicating the user has scrolled past the content.
 * A descendant marked with SCROLL_COMPLETION_ANCHOR_ATTRIBUTE replaces the
 * block's own bottom edge as that boundary (NA-363).
 *
 * Requirements:
 * - 2.1: WHEN a text block's bottom edge scrolls above the viewport bottom
 *        THEN mark that block as complete
 * - 2.2: WHEN a text block is marked complete THEN persist the completion state
 * - 2.3: WHEN a text block has already been marked complete THEN retain the complete status
 *
 * @param blockId - The unique identifier of the block to track
 * @param blockRef - React ref to the block's DOM element
 * @param onComplete - Callback fired when the block is scrolled past (idempotent)
 * @param enabled - Whether scroll tracking is enabled (default: true)
 * @param rootElement - Optional scroll container for non-window viewports
 * @param onVisible - Optional callback fired once when the block first becomes visible
 * @param overlayActive - Whether a modal/overlay currently covers the canvas.
 *        Must be in the effect deps: IntersectionObserver never re-fires when a
 *        covering element goes away, so the observer is re-created on toggle to
 *        re-evaluate blocks whose onVisible was gated while covered.
 */
export function useScrollCompletion(
  blockId: string,
  blockRef: RefObject<HTMLElement | null>,
  onComplete: (blockId: string) => void,
  enabled: boolean = true,
  rootElement?: HTMLElement | null,
  onVisible?: (blockId: string) => void,
  overlayActive?: boolean,
  pacing?: { onBottomVisible?: (blockId: string) => void; completionEnabled: boolean; resetKey?: object; hasBoundary?: boolean },
): void {
  // Track whether completion has been fired to ensure idempotency
  const hasCompletedRef = useRef(false);
  const hasVisibleRef = useRef(false);
  const hasBottomRef = useRef(false);
  const onBottomVisible = pacing?.onBottomVisible;
  const completionEnabled = pacing?.completionEnabled ?? true;
  const resetKey = pacing?.resetKey;
  // Re-select the target when Off removes the pacing anchor, without resetting completion.
  const hasBoundary = pacing?.hasBoundary;
  const trackedResetKeyRef = useRef(resetKey);
  const trackedBlockIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const element = blockRef.current;
    if (!element) {
      return;
    }

    // Reset fire-once tracking only when the tracked block actually changes —
    // other dep changes (overlay toggles, callback identity) must not re-fire
    // completion for a block that already completed.
    if (trackedBlockIdRef.current !== blockId || trackedResetKeyRef.current !== resetKey) {
      trackedResetKeyRef.current = resetKey;
      hasCompletedRef.current = false;
      hasVisibleRef.current = false;
      hasBottomRef.current = false;
      trackedBlockIdRef.current = blockId;
    }

    // NA-363: when the block designates a completion anchor, that descendant's
    // bottom edge is the completion boundary. It is observed alongside the
    // block so the observer fires at the anchor's own threshold crossings —
    // checking the anchor's rect only on the block's crossings would miss the
    // moment a small anchor becomes visible partway between them.
    const anchor = element.querySelector<HTMLElement>(`[${SCROLL_COMPLETION_ANCHOR_ATTRIBUTE}]`);
    const completionTarget = anchor ?? element;

    let active = true;
    const isAccessible = (): boolean => active && !overlayActive && document.visibilityState !== 'hidden';
    const checkBottom = (rect: DOMRectReadOnly, rootBounds?: DOMRectReadOnly | null): void => {
      if (!isAccessible()) return;
      const viewport = rootBounds ?? rootElement?.getBoundingClientRect();
      const top = (viewport?.top ?? 0) + (rootElement ? measureStickyTopBandHeight(rootElement) : 0);
      const bottom = viewport?.bottom ?? window.innerHeight;
      if (rect.bottom > bottom || rect.bottom < top) return;
      if (typeof document.elementFromPoint === 'function') {
        const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.bottom - 0.5);
        if (!hit || !element.contains(hit)) return;
      }
      // An uncovered bottom is also proof that this block has been viewed.
      // Geometry checks can precede the first IntersectionObserver delivery.
      if (!hasVisibleRef.current) {
        hasVisibleRef.current = true;
        onVisible?.(blockId);
      }
      if (!hasBottomRef.current && onBottomVisible) {
        hasBottomRef.current = true;
        onBottomVisible(blockId);
      }
      if (completionEnabled && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onComplete(blockId);
      }
    };
    const observer = new IntersectionObserver((entries) => {
      if (!isAccessible()) return;
      for (const entry of entries) {
        if (entry.target === element && !hasVisibleRef.current && isVisibleIntersectionUncovered(entry)) {
          hasVisibleRef.current = true;
          onVisible?.(blockId);
        }
        if (entry.target === completionTarget) checkBottom(entry.boundingClientRect, entry.rootBounds);
      }
    }, { root: rootElement, rootMargin: '0px', threshold: [0, 0.25, 0.5, 0.75, 1] });

    const measure = (): void => {
      const rect = completionTarget.getBoundingClientRect();
      // Empty layout is not evidence of bottom visibility (including DOM-only tests).
      if (rect.height <= 0 || rect.width <= 0) return;
      checkBottom(rect);
    };
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(element);
    if (anchor) resizeObserver?.observe(anchor);
    if (rootElement) resizeObserver?.observe(rootElement);
    const scrollTarget = rootElement ?? window;
    scrollTarget.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    document.addEventListener('visibilitychange', measure);
    measure();

    observer.observe(element);
    if (anchor) {
      observer.observe(anchor);
    }

    return () => {
      active = false;
      observer.disconnect();
      resizeObserver?.disconnect();
      scrollTarget.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      document.removeEventListener('visibilitychange', measure);
    };
  }, [blockId, blockRef, onComplete, onVisible, enabled, rootElement, overlayActive, onBottomVisible, completionEnabled, resetKey, hasBoundary]);
}
